import { createGlobalStyle } from 'styled-components'

import bgTextureBottom from '../images/bg-texture-bottom.svg'
import bgTextureLeft from '../images/bg-texture-left.svg'
import bgTextureRight from '../images/bg-texture-right.svg'
import bgTextureTop from '../images/bg-texture-top.svg'
import bgTitleLight from '../images/bg-title-light.svg'
import bgTitlePrimary from '../images/bg-title-primary.svg'
import textureDark from '../images/texture-dark.png'
import textureLight from '../images/texture-light.png'
import media from '../util/media'

export default createGlobalStyle`
  :root {
    --museosans: 'museo-sans';
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    outline: 0;
  }

  body {
    padding: 0;
    margin: 0;
    font-family: ${({ theme }) => theme.font[0]};
    color: ${({ theme }) => theme.colors.dark};
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    position: relative;

    &:before {
      content: '';
      position: fixed;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      background-image: url(${textureDark});
    }
  }

  a:not(:disabled),
  button:not(:disabled),
  [type="button"]:not(:disabled),
  [type="reset"]:not(:disabled),
  [type="submit"]:not(:disabled) {
    cursor: pointer;
  }

  a,
  button {
    transition: all .4s ease;
  }

  a {
    color: ${({ theme }) => theme.colors.dark};
    text-decoration: none;

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      text-decoration: none;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.font[1]};
    letter-spacing: 2px;
    line-height: 1;
    margin: 0;
    font-weight: normal;

    span {
      margin: 0;
    }
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
    line-height: 1.8;
  }

  address {
    font-style: normal;
    line-height: inherit;
  }

  ol,
  ul {
    padding: 0;
    list-style: none;
  }

  ol,
  ul,
  dl {
    margin: 0;
  }

  ol ol,
  ul ul,
  ol ul,
  ul ol {
    margin-bottom: 0;
  }

  blockquote {
    margin: 0 0 1rem;
  }

  b,
  strong {
    font-weight: bolder;
  }

  figure {
    margin: 0 0 1rem;
  }

  img {
    vertical-align: middle;
  }

  svg {
    overflow: hidden;
    vertical-align: middle;
  }

  table {
    border-collapse: collapse;
  }

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }

  input,
  button,
  select,
  optgroup,
  textarea {
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  button,
  input {
    overflow: visible;
  }

  button,
  select {
    text-transform: none;
  }

  select {
    word-wrap: normal;
  }

  ::-moz-focus-inner {
    padding: 0;
    border-style: none;
  }

  input[type="date"],
  input[type="time"],
  input[type="datetime-local"],
  input[type="month"] {
    -webkit-appearance: textfield;
  }

  textarea {
    overflow: auto;
    resize: vertical;
    -webkit-appearance: textfield;
  }

  fieldset {
    min-width: 0;
    padding: 0;
    margin: 0;
    border: 0;
  }

  legend {
    float: left;
    width: 100%;
    padding: 0;
    margin-bottom: 0.5rem;
    font-size: 1.5rem;
    line-height: inherit;
    color: inherit;
    white-space: normal;
  }

  mark {
    padding: 0.2em;
  }

  progress {
    vertical-align: baseline;
  }

  ::-webkit-datetime-edit {
    overflow: visible;
    line-height: 0;
  }

  [type="search"] {
    outline-offset: -2px;
    -webkit-appearance: textfield;
  }

  ::-webkit-search-decoration {
    -webkit-appearance: none;
  }

  ::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  ::-webkit-file-upload-button {
    font: inherit;
    -webkit-appearance: button;
  }

  output {
    display: inline-block;
  }

  summary {
    display: list-item;
    cursor: pointer;
  }

  template {
    display: none;
  }

  main {
    display: block;
  }

  [hidden] {
    display: none !important;
  }

  .h-100 {
    height: 100% !important;
  }

  .t-uppercase {
    text-transform: uppercase !important;
  }

  .t-lowercase {
    text-transform: lowercase !important;
  }

  .t-center {
    text-align: center !important;

    &-mobile {
      ${media.lessThan('sm')`
        text-align: center !important;
      `}
    }
  }

  .texture-light {
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url(${textureLight});
  }

  .border-texture {
    &-top:after,
    &-bottom:after {
      content: '';
      display: block;
      position: absolute;
      background-size: contain;
      z-index: 1;
    }

    &-top:after {
      width: 100%;
      height: 14px;
      background-image: url(${bgTextureTop});
      background-repeat: repeat-x;
      background-position: center top;
      top: -10px;
      left: 0;
    }

    &-bottom:after {
      width: 100%;
      height: 14px;
      background-image: url(${bgTextureBottom});
      background-repeat: repeat-x;
      background-position: center bottom;
      bottom: -10px;
      left: 0;
    }

    ${media.lessThan('xs')`
      &-top:after,
      &-bottom:after {
        background-size: cover;
      }
    `}
  }

  .highlight {
    &-light,
    &-primary {
      background-repeat: no-repeat;
      background-size: 100% 95%;
      padding: 8px 14px;
    }

    &-light {
      background-image: url(${bgTitleLight});
    }

    &-primary {
      background-image: url(${bgTitlePrimary});
    }
  }

  .duo-section {
    position: relative;

    &-left:before,
    &-right:before {
      content: '';
      position: absolute;
      z-index: -1;
      background-color: ${({ theme }) => theme.colors.primary};
    }

    &-left:after,
    &-right:after {
      content: '';
      width: 14px;
      height: 100%;
      display: block;
      position: absolute;
      background-size: contain;
      background-position: center bottom;
      background-repeat: repeat-y;
      top: 0;
      z-index: 2;
    }

    ${media.greaterThan('sm')`
      &-left:before,
      &-right:before {
        width: calc(50% + 14px);
        height: 100%;
        top: 0;
      }

      &-left:before {
        left: 0;
      }

      &-right:before {
        right: 0;
      }

      &-left:after {
        left: 50%;
        background-image: url(${bgTextureLeft});
      }

      &-right:after {
        right: 50%;
        background-image: url(${bgTextureRight});
      }
    `}

    ${media.lessThan('sm')`
      &-left:before,
      &-right:before {
        width: 100%;
        height: calc(50% - 13px);
      }

      &-left:before {
        top: 0;
      }

      &-right:before {
        bottom: 0;
      }
    `}
  }

  .fixed-duo-mobile {
    ${media.lessThan('sm')`
      &:before {
        background-color: transparent !important;
      }
    `}
  }
`
